<template>
   <fp-data-table
      :headers="headers"
      :items="items"
      :options="options"
      :hide-footer="props.simple"
      @update:options="onUpdateOptions"
      :loading="loading"
      height="auto"
    >
      <template #top>
        <v-row v-if="!simple && !short">
          <v-col cols="3">
            <fp-select
                class="py-2 px-0"
                :label="$i18n.useBranchesList.SOURCE_TYPE"
                v-model="options.sourceType"
                :items="Object.values(FeedbackSourceType)"
                @change="fetchData"
            />
          </v-col>
          <v-col cols="3">
            <fp-select
              class="py-2 px-0"
              :label="$i18n.useBranchesList.BRANCH_TYPE"
              v-model="options.branchType"
              :items="BranchTypes"
              itemName="name_pl"
              itemKey="type"
              @change="fetchData"
            />
          </v-col>
          <v-col cols="3">
            <v-switch
              :label="t.ONLY_WITH_OPINION"
              v-model="options.hasTextOpinion"
              @change="filterTextOpinion"
            />
          </v-col>
        </v-row>
        <table-pagination v-if="!props.simple" :options="options">
          <fp-filter :headers="headers" v-model="options" @input="fetchData"/>
        </table-pagination>
      </template>
      <template #item="{item}">
        <tr class="text-left">
          <td v-if="visibleElements.id">{{ item.survey.id }}</td>
          <td v-if="visibleElements.user">
            <fp-loader v-if="!usersData[item.survey.userId]" size="44px" circular/>
            <v-tooltip v-else top>
              <template v-slot:activator="{on}">
              <span v-on="on" class="cursor-pointer!">
                <person-avatar
                  @click.native.stop="goToUserDetails(item.survey.userId)"
                  :person="usersData[item.survey.userId]"
                />
              </span>
              </template>
              <p>{{ usersData[item.survey.userId].displayName }}</p>
            </v-tooltip>
          </td>
          <td v-if="visibleElements.branch">
          <span v-if="usersData[item.survey.userId]?.branch"
                @click="goToBranch(usersData[item.survey.userId].branch.id)"
                class="cursor-pointer!">
            {{ usersData[item.survey.userId].branch.name }}
          </span>
            <span v-else>-</span>
          </td>
          <td v-if="visibleElements.client">
            <v-tooltip top>
              <template v-slot:activator="{on}">
              <span v-on="on" class="cursor-pointer!">
                <person-avatar
                  @click.native.stop="goToClientDetails(item.survey.clientId)"
                  :person="{initials: clientsInitials[item.survey.clientId]}"
                />
              </span>
              </template>
              <p>{{ item.survey.clientName }}</p>
            </v-tooltip>
          </td>
          <td v-if="visibleElements.createdAt">
            <fp-date-time-view :date="item.feedback.createdAt" class="date-view"/>
          </td>
          <td v-if="visibleElements.ratings" class="rating-column py-2">
            <l-rating v-for="(config, index) in ratingsConfig"
                      :key="index"
                      v-model="item.feedback[config.model]"
                      :label="config.label"
                      :tooltip="config.tooltip"
                      :simple="props.simple"
                      :size="20"
                      class="pb-1"
                      label-class="body-3"
                      readonly/>
          </td>
          <td v-if="visibleElements.opinion">
            <p>{{ item.feedback.opinion || '-' }}</p>
          </td>
          <td v-if="visibleElements.management">
            <fp-checkbox
              v-model="item.feedback.publish"
              @input="() => onPublishChange(item)"
              :label="t.PUBLISHED"/>
            <fp-checkbox
              v-model="item.feedback.calculate"
              @input="() => onCalculateChange(item)"
              class="mt-2"
              :label="t.INCLUDED_IN_AVERAGE"/>
          </td>
          <td v-if="visibleElements.deal" @click.stop="goToDeal(item.survey.dealId)" class="cursor-pointer!">
            <span v-if="item.survey.dealId">#{{ item.survey.dealId }}</span>
            <span v-else>-</span>
          </td>
          <td v-if="visibleElements.source">{{ item.feedback.sourceType || '-' }}</td>
          <td v-if="visibleElements.lead">
            <v-icon v-if="item.survey.prospectUuid">mdi-leaf</v-icon>
            <span v-else>-</span>
          </td>
          <td v-if="visibleElements.client">
            <span>{{item.survey.clientPhone}}</span>
          </td>
          <td v-if="visibleElements.resendAction">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-btn
                    small
                    outlined
                    color="primary"
                    class="my-0"
                    :loading="resendSurveyLoadingUuid === item.survey.uuid"
                    :disabled="resendSurveyLoadingUuid === item.survey.uuid || isResendBlocked(item)"
                    @click.stop="onResendSurvey(item)"
                  >
                    {{ t.RESEND }}
                  </v-btn>
                </span>
              </template>
              <span>{{ t.RESEND_TOOLTIP }}</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </fp-data-table>
</template>

<script setup lang="ts">
import {computed, getCurrentInstance, onMounted, Ref, ref, watch} from 'vue';
import {DataTableHeader} from '@/services/searchEngine/interfaces/DataTableHeader';
import {NPSFilter} from '@/models/Filter';
import FpDataTable from '@/components/datatables/FpDataTable.vue';
import TablePagination from '@/components/commons/TablePagination.vue';
import PersonAvatar from '@/components/commons/PersonAvatar.vue';
import {NPSFeedback, NPSOpinion} from '@/modules/nps/models/NPS';
import GoToService from '@/router/GoToService';
import User from '@/models/user/User';
import UserApi from '@/modules/user/services/UserApi';
import NPSApi from '@/modules/nps/services/NPSApi';
import LRating from '@/components/stats/LRating.vue';
import {cloneDeep, isEqual} from 'lodash-es';
import FpFilter from '@/components/inputs/FpFilter.vue';
import BranchTypes from '@/modules/branches/services/BranchTypes';
import {FilterType} from '@/services/searchEngine/enums/FilterType';
import {FeedbackSourceType} from '@/modules/nps/enums/FeedbackSourceType';
import {NPSReplacementType} from '@/modules/nps/enums/NPSReplacementType';
import {useInstance} from '@/composables/useInstance';

const { $i18n, $snackbarService, $errorHandler } = useInstance();
const t = $i18n.useNPSDataTable;

type RatingConfig = {
  model: keyof NPSFeedback,
  label?: string,
  tooltip?: string,
  simple?: boolean,
}

type NPSTableElement = {
  id: number,
  user: number,
  branch: string,
  client: number,
  createdAt: string,
  ratings: Array<number>,
  opinion: string,
  management: any,
  deal: string,
  source: string,
  load: boolean,
  clientPhone: string,
  resendAction: never,
}

const props = withDefaults(defineProps<{
  opinions?: Array<NPSOpinion>;
  params?: NPSFilter,
  short?: boolean;
  simple?: boolean;
  showResendAction?: boolean;
  resendSurveyLoadingUuid?: string | null;
}>(), {
  showResendAction: false,
  resendSurveyLoadingUuid: null,
});
const emit = defineEmits(['resend-survey',]);

const MAX_INITIALS_LENGTH = 3;

const items: Ref<Array<NPSOpinion>> = ref([]);
const users: Ref<Array<User>> = ref([]);
const options = ref<NPSFilter>(new NPSFilter());
const oldOptions: Ref<NPSFilter> = ref(cloneDeep(options.value));
const loading: Ref<boolean> = ref(false);
const instance = getCurrentInstance();

const headersConfig = computed<Array<DataTableHeader<NPSTableElement, NPSFilter>>>(() => {
  return [
    {text: t.ID, value: 'id', valueSort: 'id', condition: !props.short,},
    {
      text: t.EXPERT,
      value: 'user',
      valueSort: 'userIds',
      valueFilter: 'users',
      filterType: FilterType.AUTOCOMPLETE,
      condition: !props.short,
    },
    {text: t.BRANCH, value: 'branch', valueSort: 'branch', condition: !props.short, },
    {text: t.CLIENT, value: 'client', },
    {text: t.CREATED_AT, value: 'createdAt', valueFilter: 'createdAt', filterType: FilterType.DATE, valueSort: 'createdAt' && (!props.short && !props.simple) , },
    {text: t.RATINGS, value: 'ratings', valueFilter: 'rating', filterType: FilterType.RANGE, range: {from: 1, to: 5,},},
    {text: t.OPINION, value: 'opinion', },
    {text: t.MANAGEMENT, value: 'management', condition: !props.short, },
    {text: t.DEAL, value: 'deal', },
    {text: t.SOURCE, value: 'source', valueSort: 'source', condition: !props.short, },
    {text: t.LEAD, value: 'lead', condition: !props.short, },
    {text: t.CLIENT_PHONE, value: 'clientPhone', valueFilter: 'clientPhone', filterType: FilterType.TEXT,},
    {text: t.RESEND_ACTION_COLUMN, value: 'resendAction', condition: !!props.showResendAction, },
  ];
});

const ratingsConfig = computed<Array<RatingConfig>>(() => {
  return [
    {
      model: 'substantivePartOfMeeting',
      label: t.KNOWLEDGE,
    },
    {
      model: 'expertInvolvement',
      label: t.INVOLVEMENT,
    },
    {
      model: 'expertBehavior',
      label: t.PERSONAL_CULTURE,
    },
  ];
});

const visibleElements = computed<Partial<Record<keyof NPSTableElement, boolean>>>(() => {
  const visibilityConfig: Partial<Record<keyof NPSTableElement, boolean>> = {};
  headersConfig.value.map(item => {
    const key = item.value as keyof NPSTableElement;
    visibilityConfig[key] = item.condition === undefined || item.condition;
  });

  return visibilityConfig;
});

const headers = computed<Array<DataTableHeader<NPSTableElement, NPSFilter>>>(() => {
  return headersConfig.value
    .filter(item => item.condition === undefined || item.condition)
    .map(x => new DataTableHeader<NPSTableElement, NPSFilter>(x));
});

onMounted(() => {
  if (props.params) {
    options.value = props.params;
    cloneOptions();
  }

  if (props.opinions) {
    items.value = props.opinions;
    fetchUsers();
  } else {
    fetchData();
  }
});

watch(() => props.opinions, (opinions) => {
  if (!opinions) {
    return;
  }

  items.value = opinions;
  fetchUsers();
}, {deep: true,});

const onUpdateOptions = (newValue: NPSFilter) => {
  if (isEqual(cloneDeep(oldOptions.value), cloneDeep(options.value))) {
    return;
  }

  if (!props.simple && options.value.page) {
    GoToService.setQuery({page: String(options.value.page), }, false);
  }

  cloneOptions();
  fetchData();
};

const filterTextOpinion = async() => {
  if (!options.value.hasTextOpinion) delete options.value.hasTextOpinion;
  await fetchData();
};

const fetchData = async() => {
  loading.value = true;
  try {
    options.value.page = instance?.proxy.$route.query.page ? Number(instance?.proxy.$route.query.page) : 1;
    const response = await NPSApi.getOpinions(options.value);
    items.value = response.items;
    options.value.quantity = response.quantity;
    options.value.page = response.page || options.value.page;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }

  cloneOptions();
  fetchUsers();
};

const getUsersIdsToFetch = () => {
  const usersIds: Array<number> = [];
  items.value.map(item => {
    if (!usersData.value[item.survey.userId] && !usersIds.includes(item.survey.userId)) {
      usersIds.push(item.survey.userId);
    }
  });

  return usersIds;
};

const fetchUsers = async() => {
  if (props.short) {
    return;
  }

  const usersIds = getUsersIdsToFetch();
  if (!usersIds.length) {
    return;
  }

  const response = await UserApi.usersForFilters({users: usersIds,});
  if (response) {
    users.value.push(...response);
  }
};

const usersData = computed<Record<number, User>>(() => {
  const usersMap: Record<number, User> = {};
  users.value.map(user => {
    usersMap[user.id] = user;
  });

  return usersMap;
});

const clientsInitials = computed<Record<number, string>>(() => {
  const initials: Record<number, string> = {};

  items.value.map(item => {
    initials[item.survey.clientId] = (item.survey.clientName.split(' ').map(x => x.substring(0, 1).toUpperCase()).join('') || '').substring(0,MAX_INITIALS_LENGTH);
  });

  return initials;
});

const goToUserDetails = (userId: number) => {
  if (userId) {
    GoToService.user(userId);
  }
};

const goToClientDetails = (clientId: number) => {
  if (clientId) {
    GoToService.client(clientId);
  }
};

const goToDeal = (dealId: number) => {
  if (dealId) {
    GoToService.deal(dealId);
  }
};

const goToBranch = (branchId: number) => {
  if (branchId) {
    GoToService.branch(branchId);
  }
};

const onPublishChange = (item: NPSOpinion) => {
  item.feedback!.calculate = item.feedback!.publish;
  processOpinion(item, item.feedback!.publish, item.feedback!.calculate);
};

const onCalculateChange = (item: NPSOpinion) => {
  processOpinion(item, item.feedback!.publish, item.feedback!.calculate);
};

const processOpinion = async(item: NPSOpinion, publish: boolean = false, calculate: boolean = false) => {
  try {
    await NPSApi.pocessOpinion(item.feedback!.id, { publish, calculate, });
    $snackbarService.openSuccessSnackbar(t.OPINION_STATUS_CHANGED);
  } catch (e) {
    $errorHandler(e, t.OPINION_PROCESS_ERROR);
  }
};

const isResendBlocked = (item: NPSOpinion) => {
  return item.survey.replacement === NPSReplacementType.CanNotBeReplaced || !!item.survey.replacementId?.trim();
};

const onResendSurvey = (item: NPSOpinion) => {
  emit('resend-survey', item);
};

const cloneOptions = () => {
  oldOptions.value = cloneDeep(options.value);
};
</script>

<style scoped lang="scss">
.date-view :deep(.v-list-item ) {
  padding-left: 0;
  padding-right: 0;
}
:deep(.rating-column) {
  .v-rating .v-icon {
    padding: 2px;
    &:first-child {
      padding-left: 0;
    }
    &:last-child {
      padding-right: 0;
    }
  }
  .col {
    line-height: 1rem;
    &:last-child {
      margin-top: 0 !important;
    }
  }
}
</style>
